import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { getSupabaseUrl, supabase } from '../lib/supabase';
import { logger } from '../utils/Logger';
import { hasGoogleIdentity, isPropellerDomain, isStaffRole } from '../utils/ssoPolicy';
import { useNavigationStore } from '../stores/navigationStore';
import { signOutWithNavigationIsolation } from '../utils/authNavigationIsolation';
import {
  advanceAuthIdentityFence,
  canApplyStaffSsoPolicy,
  type AuthIdentityFence,
} from '../utils/staffSsoPolicyFence';


interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  policyError: string | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: (redirectTo?: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function bindNavigationPreferencesToSession(session: Session | null): void {
  useNavigationStore.getState().bindNavigationPreferencesOwner(session?.user?.id ?? null);
}

/**
 * Hostnames where staff Google-SSO enforcement is mandatory and no build-time flag may
 * turn it off. Kept as data, and as a Set, so adding a surface is one line and cannot be
 * confused with the staging heuristics below it.
 */
const PRODUCTION_STAFF_HOSTS = new Set(['platform.propellertravel.com']);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const signOutInProgressRef = useRef(false);
  const authIdentityGenerationRef = useRef(0);
  const currentAuthUserIdRef = useRef<string | null>(null);
  const currentAuthSessionTokenRef = useRef<string | null>(null);
  const staffPolicyAbortRef = useRef<AbortController | null>(null);
  const enforceStaffSsoPolicyRef = useRef<(
    session: Session,
    expectedIdentity: AuthIdentityFence,
  ) => Promise<void>>(() => Promise.resolve());
  const sessionRestoreBlockedRef = useRef(false);

  const currentAuthIdentityFence = (): AuthIdentityFence => ({
    userId: currentAuthUserIdRef.current,
    sessionToken: currentAuthSessionTokenRef.current,
    generation: authIdentityGenerationRef.current,
  });

  const acceptAuthIdentityTransition = (
    nextSession: Session | null,
  ): AuthIdentityFence => {
    staffPolicyAbortRef.current?.abort();
    staffPolicyAbortRef.current = null;
    const next = advanceAuthIdentityFence(
      authIdentityGenerationRef.current,
      nextSession?.user?.id ?? null,
      nextSession?.access_token ?? null,
    );
    authIdentityGenerationRef.current = next.generation;
    currentAuthUserIdRef.current = next.userId;
    currentAuthSessionTokenRef.current = next.sessionToken;
    bindNavigationPreferencesToSession(nextSession);
    setSession(nextSession);
    setUser(previous => previous?.id === next.userId ? previous : (nextSession?.user ?? null));
    return next;
  };

  const performIsolatedSignOutRef = useRef(async (nextPolicyError: string | null) => {
    signOutInProgressRef.current = true;
    sessionRestoreBlockedRef.current = true;
    const signOutIdentity = acceptAuthIdentityTransition(null);

    try {
      await signOutWithNavigationIsolation(
        () => supabase.auth.signOut(),
        () => {
          currentAuthUserIdRef.current = null;
          currentAuthSessionTokenRef.current = null;
          setSession(null);
          setUser(null);
          setPolicyError(nextPolicyError);
        },
        () => bindNavigationPreferencesToSession(null),
      );
    } catch (error) {
      logger.warn('[Auth] Sign-out request failed after local identity isolation', {
        error: String(error),
      });
    } finally {
      if (authIdentityGenerationRef.current === signOutIdentity.generation) {
        signOutInProgressRef.current = false;
      }
    }
  });

  useEffect(() => {
    let mounted = true;
    const initialIdentityGeneration = authIdentityGenerationRef.current;

    const decodeJwtPayload = (jwt: string) => {
      const payload = jwt.split('.')[1] ?? '';
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
      return JSON.parse(decodeURIComponent(
        Array.from(atob(padded), c => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')
      ));
    };

    const persistCallbackSession = async (params: URLSearchParams): Promise<Session> => {
      const accessToken = params.get('access_token') ?? '';
      const refreshToken = params.get('refresh_token') ?? '';
      const providerToken = params.get('provider_token') ?? undefined;
      const claims = decodeJwtPayload(accessToken);
      const nowIso = new Date().toISOString();
      const user = {
        id: claims.sub,
        aud: claims.aud ?? 'authenticated',
        role: claims.role ?? 'authenticated',
        email: claims.email,
        phone: claims.phone ?? '',
        app_metadata: claims.app_metadata ?? { provider: 'google', providers: ['google'] },
        user_metadata: claims.user_metadata ?? {},
        identities: [{
          provider: 'google',
          user_id: claims.user_metadata?.provider_id ?? claims.sub,
          identity_data: claims.user_metadata ?? {},
          id: claims.sub,
          created_at: nowIso,
          updated_at: nowIso,
        }],
        created_at: nowIso,
        updated_at: nowIso,
        confirmed_at: nowIso,
        email_confirmed_at: nowIso,
        last_sign_in_at: nowIso,
        is_anonymous: false,
      } as User;

      const session = {
        access_token: accessToken,
        refresh_token: refreshToken,
        provider_token: providerToken,
        token_type: params.get('token_type') ?? 'bearer',
        expires_in: Number(params.get('expires_in') ?? 3600),
        expires_at: Number(params.get('expires_at') ?? Math.floor(Date.now() / 1000) + 3600),
        user,
      } as Session;

      const supabaseRef = new URL(getSupabaseUrl()).hostname.split('.')[0];
      localStorage.setItem(`sb-${supabaseRef}-auth-token`, JSON.stringify(session));
      sessionStorage.setItem('pt-google-sso-callback-user', user.id);
      window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);

      try {
        const setSessionPromise = supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        const setSessionTimeout = new Promise<null>((resolve) => {
          window.setTimeout(() => resolve(null), 8000);
        });
        const result = await Promise.race([setSessionPromise, setSessionTimeout]);
        if (result?.data?.session) {
          const officialSession = {
            ...result.data.session,
            provider_token: providerToken ?? (result.data.session as any).provider_token,
          } as Session;
          localStorage.setItem(`sb-${supabaseRef}-auth-token`, JSON.stringify(officialSession));
          return officialSession;
        }
      } catch (error) {
        logger.warn('[Auth] Supabase callback session persistence failed; using parsed callback session', { error: String(error) });
      }

      return session;
    };

    const readStoredSession = (): Session | null => {
      try {
        const supabaseRef = new URL(getSupabaseUrl()).hostname.split('.')[0];
        const raw = localStorage.getItem(`sb-${supabaseRef}-auth-token`);
        if (!raw) return null;

        const storedSession = JSON.parse(raw) as Session;
        if (!storedSession?.access_token || !storedSession?.refresh_token || !storedSession?.expires_at) {
          return null;
        }

        if (storedSession.expires_at <= Math.floor(Date.now() / 1000)) {
          return null;
        }

        return storedSession;
      } catch {
        return null;
      }
    };

    const resolveInitialSession = async () => {
      if (typeof window !== 'undefined' && window.location.hash.includes('access_token=')) {
        const params = new URLSearchParams(window.location.hash.slice(1));
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');

        if (accessToken && refreshToken) {
          return await persistCallbackSession(params);
        }
      }

      const { data: { session } } = await supabase.auth.getSession();
      return session;
    };

    const sessionTimeout = new Promise<Session | null>((resolve) => {
      window.setTimeout(() => resolve(readStoredSession()), 5000);
    });

    const hasAuthCallback = typeof window !== 'undefined' && window.location.hash.includes('access_token=');
    const initialSessionPromise = hasAuthCallback
      ? resolveInitialSession()
      : Promise.race([
          resolveInitialSession(),
          sessionTimeout,
        ]);

    initialSessionPromise
      .then((session) => {
        if (
          !mounted ||
          signOutInProgressRef.current ||
          sessionRestoreBlockedRef.current ||
          initialIdentityGeneration !== authIdentityGenerationRef.current
        ) return;
        const acceptedIdentity = acceptAuthIdentityTransition(session);
        // Policy enforcement runs on auth state changes, but do a first pass here too.
        if (session) {
          void enforceStaffSsoPolicyRef.current(session, acceptedIdentity);
        }
      })
      .catch((error) => {
        logger.warn('[Auth] Initial session check failed', { error: String(error) });
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      logger.info('[Auth] State change', { event, email: session?.user?.email });

      if (signOutInProgressRef.current) return;
      if (event === 'SIGNED_IN') sessionRestoreBlockedRef.current = false;
      if (sessionRestoreBlockedRef.current && session) return;

      // Supabase emits INITIAL_SESSION when it restores or parses an OAuth
      // callback session on page load. Treat it as an authenticated state so
      // users do not bounce back to the login screen after Google redirects.
      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
        const acceptedIdentity = acceptAuthIdentityTransition(session);
        if (event === 'SIGNED_OUT') {
          setPolicyError(null);
        } else if (session) {
          void enforceStaffSsoPolicyRef.current(session, acceptedIdentity);
        }
      } else if (event === 'TOKEN_REFRESHED' && session) {
        // On token refresh, only update state — DO NOT re-run enforceStaffSsoPolicy.
        // The user's identity list (providers linked to the account) does not change
        // on token refresh, and transient deserialisation quirks in the refreshed
        // session could omit the Google identity for a few ms, causing the SSO
        // policy check to force a signOut and bounce the user through the login
        // flow repeatedly. Staff SSO is enforced on SIGNED_IN and USER_UPDATED only,
        // which is where identity can actually change.
        acceptAuthIdentityTransition(session);
      }
    });

    return () => {
      mounted = false;
      staffPolicyAbortRef.current?.abort();
      subscription.unsubscribe();
    };
  }, []);

  const enforceStaffSsoPolicy = async (
    session: Session,
    expectedIdentity: AuthIdentityFence,
  ) => {
    const controller = new AbortController();
    staffPolicyAbortRef.current?.abort();
    staffPolicyAbortRef.current = controller;
    const canCommitPolicy = () => canApplyStaffSsoPolicy(
      expectedIdentity,
      currentAuthIdentityFence(),
      controller.signal,
    );
    try {
      const signedInUser = session?.user;
      if (!signedInUser || !canCommitPolicy()) return;

      // Skip SSO enforcement in staging to allow email/password login for testing.
      //
      // The production staff host is checked FIRST and wins outright. It used to be one
      // term among six, which meant a build-time flag could switch off an auth control on
      // the live platform -- and did. platform.propellertravel.com was served by a build
      // compiled as a Vercel PREVIEW, so Vite inlined
      // `import.meta.env.VITE_VERCEL_ENV === 'preview'` as a constant and this whole
      // condition became:
      //
      //   hostname.includes('staging') || … || hostname === '127.0.0.1' || true
      //
      // Every hostname term above it was unreachable and staff Google-SSO enforcement was
      // dead code in production. Confirmed by reading the shipped bundle, not inferred.
      //
      // An environment variable must not be able to disable an auth control on a real
      // production hostname. The host is the fact; the env var is a hint about the build.
      // When they disagree the host wins. Local and staging development are unaffected.
      const hostname = window.location.hostname;
      const isProductionStaffHost = PRODUCTION_STAFF_HOSTS.has(hostname);
      const isStaging =
        !isProductionStaffHost &&
        (hostname.includes('staging') ||
          hostname.includes('preview') ||
          hostname === 'localhost' ||
          hostname === '127.0.0.1' ||
          import.meta.env.VITE_ENVIRONMENT === 'staging' ||
          import.meta.env.VITE_VERCEL_ENV === 'preview');

      if (isStaging) {
        logger.info('[Auth] Skipping SSO enforcement in staging environment');
        return;
      }

      // Skip on customer-facing and partner-facing surfaces — staff-SSO is
      // strictly for platform.propellertravel.com. The three identity axes
      // (staff, pax, partner) are orthogonal: a single auth.users row can
      // be all three. Each axis has its own auth surface and never shares
      // SSO policy with another.
      //
      //   pax.*      → customers.pax_user_id, email/password
      //   partner.*  → partners.auth_user_id, partner-specific auth (TBD)
      //   platform.* → user_profiles.role + Google SSO (this enforcement)
      if (hostname.startsWith('pax.') || hostname.startsWith('partner.')) {
        logger.info('[Auth] Skipping staff SSO enforcement on customer/partner surface', { hostname });
        return;
      }

      // Fetch role from user_profiles (source of truth).
      const { data, error } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('user_id', signedInUser.id)
        .abortSignal(controller.signal)
        .maybeSingle();

      if (!canCommitPolicy()) return;
      if (error) {
        logger.warn('[Auth] Could not load user role for SSO policy check', { error: error.message });
        return;
      }

      const role = (data as any)?.role as string | undefined;
      if (!isStaffRole(role)) {
        // Non-staff accounts are not subject to staff SSO enforcement.
        return;
      }

      if (role === 'super_admin') {
        // Avoid locking out break-glass/admin verification sessions when
        // provider metadata is temporarily incomplete during OAuth restore.
        return;
      }

      const email = signedInUser.email ?? '';
      if (!isPropellerDomain(email)) {
        if (!canCommitPolicy()) return;
        await performIsolatedSignOutRef.current('Staff accounts must use an @propellertravel.com email address.');
        return;
      }

      const hasGoogleSsoEvidence =
        hasGoogleIdentity(signedInUser) ||
        Boolean((session as any)?.provider_token) ||
        (signedInUser.user_metadata as any)?.iss === 'https://accounts.google.com' ||
        (signedInUser.user_metadata as any)?.custom_claims?.hd === 'propellertravel.com' ||
        sessionStorage.getItem('pt-google-sso-callback-user') === signedInUser.id;

      if (!hasGoogleSsoEvidence) {
        if (!canCommitPolicy()) return;
        await performIsolatedSignOutRef.current('Staff accounts must sign in using Google SSO.');
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      logger.error('[Auth] Error enforcing staff SSO policy', { err: String(err) });
    } finally {
      if (staffPolicyAbortRef.current === controller) {
        staffPolicyAbortRef.current = null;
      }
    }
  };
  enforceStaffSsoPolicyRef.current = enforceStaffSsoPolicy;

  const signIn = async (email: string, password: string) => {
    try {
      setPolicyError(null);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signInWithGoogle = async (redirectPath?: string) => {
    try {
      setPolicyError(null);
      const safeRedirectPath = redirectPath && redirectPath.startsWith('/') && !redirectPath.startsWith('//')
        ? redirectPath
        : '/';
      const redirectTo = `${window.location.origin}${safeRedirectPath}`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          // Hint Google to use Propeller Workspace accounts.
          // Note: this is a UX hint; we still enforce domain + google identity after sign-in.
          queryParams: { hd: 'propellertravel.com', prompt: 'select_account' },
        },
      });
      return { error: error as any };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      // The database creates the profile when the account is created. The browser must
      // never write a role; this call used to insert a profile with role 'agent'.
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) return { error };

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    await performIsolatedSignOutRef.current(null);
  };

  const value = {
    user,
    session,
    loading,
    policyError,
    signIn,
    signInWithGoogle,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/** Like useAuth, but undefined outside an AuthProvider instead of throwing (for a widget that only needs to know whether someone is signed in). */
export function useOptionalAuth() {
  return useContext(AuthContext);
}
